(() => {
  if (window.darchiCartSprinkles) return;
  window.darchiCartSprinkles = true;

  const selector = '.darchi-card-action.button, .quick-add__submit.button, .product-form__submit.button';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const states = new WeakMap();
  const pending = new Set();
  let frame = 0;
  const patterns = [
    [[8,47,2.6,.87],[20,22,1.7,.58],[29,72,2,.78],[39,40,3,.91],[53,86,1.4,.55],[61,17,2.2,.71],[75,55,1.6,.61],[87,28,1.2,.48],[12,77,1.4,.54],[49,58,1.2,.43],[80,83,1.9,.7],[39,9,1.2,.45]],
    [[89,53,2.7,.88],[73,19,2,.75],[64,78,1.6,.56],[53,42,2.9,.9],[41,11,1.3,.51],[30,69,2.2,.77],[18,30,1.5,.55],[7,57,1.2,.44],[90,81,1.3,.52],[73,48,1.2,.43],[48,90,1.8,.63],[91,24,1.1,.42]]
  ];

  const blocked = button => button.matches(':disabled, [aria-disabled="true"], [aria-busy="true"], .loading');
  const findButton = target => target instanceof Element ? target.closest(selector) : target.parentElement?.closest(selector);

  function reset(button) {
    const state = states.get(button);
    if (!state) return;
    if (state.pointerFrame) cancelAnimationFrame(state.pointerFrame);
    if (state.tapTimer) clearTimeout(state.tapTimer);
    state.pointerFrame = 0;
    state.tapTimer = 0;
    state.tapping = false;
    if (button.classList.contains('darchi-sprinkle-tapping')) button.classList.remove('darchi-sprinkle-tapping');
    button.style.setProperty('--darchi-drift-x', '0px');
    button.style.setProperty('--darchi-drift-y', '0px');
    state.animations.forEach(animation => animation.cancel());
    state.animations = [];
  }

  function measure(button) {
    if (!button.isConnected) return;
    if (blocked(button) && !states.get(button)?.tapping) reset(button);
    const bounds = button.getBoundingClientRect();
    if (!bounds.width) return;
    const range = document.createRange();
    const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.textContent.trim() || node.parentElement.closest('.darchi-sprinkle-field, .loading__spinner, .hidden, [hidden], [aria-hidden="true"]')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    let left = bounds.right;
    let right = bounds.left;
    let top = bounds.bottom;
    let bottom = bounds.top;
    let node;
    while ((node = walker.nextNode())) {
      range.selectNodeContents(node);
      for (const rect of range.getClientRects()) {
        if (rect.width) {
          left = Math.min(left, rect.left);
          right = Math.max(right, rect.right);
          top = Math.min(top, rect.top);
          bottom = Math.max(bottom, rect.bottom);
        }
      }
    }
    const width = Math.max(0, Math.min(92, bounds.width * .18, left - bounds.left - 21, bounds.right - right - 21));
    const compact = width < 7;
    const state = states.get(button);
    if (state) state.compact = compact;
    if (button.classList.contains('darchi-sprinkle-compact') !== compact) button.classList.toggle('darchi-sprinkle-compact', compact);
    button.style.setProperty('--darchi-sprinkle-top-height', `${Math.max(1, Math.min(6, top - bounds.top - 9)).toFixed(1)}px`);
    button.style.setProperty('--darchi-sprinkle-bottom-height', `${Math.max(1, Math.min(6, bounds.bottom - bottom - 9)).toFixed(1)}px`);
    button.style.setProperty('--darchi-sprinkle-width', `${width.toFixed(1)}px`);
    button.style.setProperty('--darchi-sprinkle-visibility', 'visible');
  }

  function queue(button) {
    if (!button) return;
    pending.add(button);
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      pending.forEach(item => { initialize(item); measure(item); });
      pending.clear();
    });
  }

  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => entries.forEach(entry => queue(entry.target))) : null;

  function initialize(button) {
    if (!button.isConnected) return;
    if (states.has(button) && button.querySelectorAll(':scope > .darchi-sprinkle-field').length === 2) return;
    reset(button);
    const dots = [];
    const state = { dots, animations: [], pointerFrame: 0, tapping: false, tapTimer: 0, compact: false };
    states.set(button, state);
    button.querySelectorAll(':scope > .darchi-sprinkle-field').forEach(field => field.remove());
    patterns.forEach((pattern, side) => {
      const field = document.createElement('span');
      field.className = `darchi-sprinkle-field${side ? ' darchi-sprinkle-field--right' : ''}`;
      field.setAttribute('aria-hidden', 'true');
      pattern.forEach(([x, y, size, opacity], index) => {
        const dot = document.createElement('span');
        dot.className = 'darchi-sprinkle-dot';
        dot.style.setProperty('--darchi-dot-x', `${x}%`);
        dot.style.setProperty('--darchi-dot-y', `${y}%`);
        dot.style.setProperty('--darchi-dot-size', `${size}px`);
        dot.style.setProperty('--darchi-dot-opacity', opacity);
        dot.style.setProperty('--darchi-dot-depth', .55 + (index % 4) * .2);
        field.append(dot);
        dots.push({ element: dot, x, y, side });
      });
      button.append(field);
    });
    resize?.observe(button);
  }

  const nearViewport = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      nearViewport.unobserve(entry.target);
      queue(entry.target);
    });
  }, { rootMargin: '300px 0px' }) : null;

  function discover(button) {
    if (!button || states.has(button)) return;
    if (nearViewport) nearViewport.observe(button);
    else queue(button);
  }

  function initializeNow(button) {
    const ready = states.has(button);
    nearViewport?.unobserve(button);
    initialize(button);
    if (!ready) measure(button);
  }

  function scan(root) {
    if (!(root instanceof Element || root instanceof Document)) return;
    if (root instanceof Element && root.matches(selector)) discover(root);
    root.querySelectorAll(selector).forEach(discover);
  }

  document.addEventListener('pointermove', event => {
    if (event.pointerType !== 'mouse' || reducedMotion.matches) return;
    const button = findButton(event.target);
    if (!button || blocked(button)) return;
    initializeNow(button);
    const state = states.get(button);
    state.pointerX = event.clientX;
    state.pointerY = event.clientY;
    if (state.pointerFrame) return;
    state.pointerFrame = requestAnimationFrame(() => {
      state.pointerFrame = 0;
      if (!button.isConnected || blocked(button) || reducedMotion.matches) return;
      const rect = button.getBoundingClientRect();
      const x = Math.max(-1, Math.min(1, (state.pointerX - rect.left) / rect.width * 2 - 1));
      const y = Math.max(-1, Math.min(1, (state.pointerY - rect.top) / rect.height * 2 - 1));
      button.style.setProperty('--darchi-drift-x', `${(x * (state.compact ? 2 : 3.2)).toFixed(2)}px`);
      button.style.setProperty('--darchi-drift-y', `${(y * (state.compact ? .25 : 2.4)).toFixed(2)}px`);
    });
  }, { passive: true });

  document.addEventListener('pointerout', event => {
    if (event.pointerType !== 'mouse') return;
    const button = findButton(event.target);
    if (button && !button.contains(event.relatedTarget)) reset(button);
  }, { passive: true });

  document.addEventListener('pointerdown', event => {
    if (event.pointerType === 'mouse' || reducedMotion.matches) return;
    const button = findButton(event.target);
    if (!button || blocked(button)) return;
    initializeNow(button);
    reset(button);
    const state = states.get(button);
    state.tapping = true;
    button.classList.add('darchi-sprinkle-tapping');
    state.animations = state.dots.map(({ element, x, y, side }) => {
      if (typeof element.animate !== 'function') return null;
      const dx = (side ? 1 : -1) * (1.5 + x / 100);
      const dy = (y - 50) / (state.compact ? 180 : 22);
      return element.animate([
        { transform: 'translate(-50%, -50%) translate(0px, 0px)' },
        { transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px)`, offset: .4 },
        { transform: 'translate(-50%, -50%) translate(0px, 0px)' }
      ], { duration: 1100, easing: 'cubic-bezier(.22,.61,.36,1)' });
    }).filter(Boolean);
    state.tapTimer = setTimeout(() => {
      state.tapTimer = 0;
      state.tapping = false;
      state.animations = [];
      button.classList.remove('darchi-sprinkle-tapping');
      queue(button);
    }, 1100);
  }, { passive: true });

  document.addEventListener('pointercancel', event => {
    const button = findButton(event.target);
    if (button) reset(button);
  }, { passive: true });
  document.addEventListener('shopify:section:load', event => scan(event.target));
  reducedMotion.addEventListener('change', () => document.querySelectorAll(selector).forEach(reset));

  const observer = new MutationObserver(records => {
    records.forEach(record => {
      if (record.target instanceof Element && record.target.closest('.darchi-sprinkle-field')) return;
      if (record.type === 'childList') {
        record.addedNodes.forEach(node => {
          if (node instanceof Element && !node.matches('.darchi-sprinkle-field')) scan(node);
        });
        record.removedNodes.forEach(node => {
          if (!(node instanceof Element) || node.isConnected) return;
          const buttons = node.matches(selector) ? [node] : [...node.querySelectorAll(selector)];
          buttons.forEach(button => { nearViewport?.unobserve(button); reset(button); resize?.unobserve(button); states.delete(button); });
        });
      }
      const button = findButton(record.target);
      if (button) queue(button);
    });
  });
  observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled', 'aria-busy'] });
  scan(document);
  document.fonts?.ready.then(() => document.querySelectorAll(selector).forEach(button => { if (states.has(button)) queue(button); }));
  if (!resize) window.addEventListener('resize', () => document.querySelectorAll(selector).forEach(button => { if (states.has(button)) queue(button); }), { passive: true });
})();
